// Selector de adjuntos compartido por el CRM de Operations: el composer de notas
// del detalle de ticket (TicketDetail → Comments) y el modal "Nuevo ticket"
// (CreateTicketModal). Extrae la lógica que vivía inline en Comments: lectura a
// base64, validación de límites (MAX_ATTACHMENTS / MAX_ATTACHMENT_BYTES /
// MAX_ATTACHMENTS_TOTAL_BYTES) y la UI de chips + botón "📎 Adjuntar".
//
// Forma: un hook `useAttachmentPicker()` con el estado + handlers, y un
// componente presentacional `AttachmentPickerUI` que pinta chips, el botón de
// adjuntar (con input file oculto) y el error de validación. El consumidor
// decide cuándo publicar y con `toBodyAttachments()` obtiene el array que espera
// el contrato (sin el `size`, que es solo para la UI).

import { useCallback, useRef, useState } from "react";
import {
  MAX_ATTACHMENTS,
  MAX_ATTACHMENT_BYTES,
  MAX_ATTACHMENTS_TOTAL_BYTES,
  NewTicketAttachment,
} from "../../api/crmTypes";
import { ErrorBox } from "../FormControls";

// Adjunto local ya leído a base64, en espera de publicarse con la nota. El
// `size` es solo para la UI (chip + validación de total); no viaja al contrato.
export type LocalAttachment = NewTicketAttachment & { size: number };

// Lee un File como base64 puro (sin el prefijo data:...;base64,). Mismo patrón
// que el composer de notas del CRM (readAsDataURL + recorte tras la coma).
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result as string;
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

export type AttachmentPicker = {
  attachments: LocalAttachment[];
  attachError: string | null;
  /** onChange del <input type="file">; valida límites y lee a base64. */
  onFilesPicked: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  /**
   * Añade archivos como adjuntos (valida los mismos límites) y devuelve los
   * filenames aceptados, o `null` si algún límite se superó (setea el error y
   * no añade nada). Lo usa el pegado de imágenes inline: renombra cada imagen a
   * un filename único y necesita ese nombre para insertar el token en el body.
   */
  addFiles: (picked: File[]) => Promise<string[] | null>;
  /**
   * Añade adjuntos YA leídos a base64 (mismas validaciones que addFiles). Lo
   * usa el paso rico→sencillo del NoteEditor: las capturas que viven como
   * data-URI en el editor pasan al pipeline de pendientes para que sus tokens
   * sigan resolviendo. Devuelve los filenames aceptados o `null` si un límite
   * se superó (el error queda pintado y no se añade nada).
   */
  addLocal: (added: LocalAttachment[]) => string[] | null;
  /** Quita el adjunto en la posición `index`. */
  remove: (index: number) => void;
  /** Vacía la selección y el error (tras publicar con éxito). */
  clear: () => void;
  /** Array de adjuntos en la forma del contrato (sin `size`). */
  toBodyAttachments: () => NewTicketAttachment[];
};

export function useAttachmentPicker(): AttachmentPicker {
  const [attachments, setAttachments] = useState<LocalAttachment[]>([]);
  const [attachError, setAttachError] = useState<string | null>(null);

  // Valida los límites de una incorporación (count/size por archivo/total).
  // Devuelve el mensaje de error o null si cabe. Compartida por addFiles
  // (con File.size, ANTES de leer a base64) y addLocal (con LocalAttachment).
  const validateAdd = useCallback(
    (incoming: { name: string; size: number }[]): string | null => {
      if (attachments.length + incoming.length > MAX_ATTACHMENTS) {
        return `Máximo ${MAX_ATTACHMENTS} adjuntos por nota`;
      }
      const oversize = incoming.find((f) => f.size > MAX_ATTACHMENT_BYTES);
      if (oversize) {
        return `${oversize.name} supera ${formatBytes(MAX_ATTACHMENT_BYTES)}`;
      }
      const total =
        attachments.reduce((s, a) => s + a.size, 0) +
        incoming.reduce((s, f) => s + f.size, 0);
      if (total > MAX_ATTACHMENTS_TOTAL_BYTES) {
        return `Los adjuntos superan ${formatBytes(
          MAX_ATTACHMENTS_TOTAL_BYTES,
        )} en total`;
      }
      return null;
    },
    [attachments],
  );

  const addLocal = useCallback(
    (added: LocalAttachment[]): string[] | null => {
      if (added.length === 0) return [];
      setAttachError(null);
      const err = validateAdd(
        added.map((a) => ({ name: a.filename, size: a.size })),
      );
      if (err) {
        setAttachError(err);
        return null;
      }
      setAttachments((prev) => [...prev, ...added]);
      return added.map((a) => a.filename);
    },
    [validateAdd],
  );

  const addFiles = useCallback(
    async (picked: File[]): Promise<string[] | null> => {
      if (picked.length === 0) return [];
      setAttachError(null);
      const err = validateAdd(picked);
      if (err) {
        setAttachError(err);
        return null;
      }
      try {
        const added: LocalAttachment[] = await Promise.all(
          picked.map(async (f) => ({
            filename: f.name,
            contentType: f.type || "application/octet-stream",
            contentBase64: await fileToBase64(f),
            size: f.size,
          })),
        );
        setAttachments((prev) => [...prev, ...added]);
        return added.map((a) => a.filename);
      } catch {
        setAttachError("No se pudo leer el archivo");
        return null;
      }
    },
    [validateAdd],
  );

  const onFilesPicked = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      e.target.value = ""; // permite re-seleccionar el mismo archivo
      await addFiles(picked);
    },
    [addFiles],
  );

  const remove = useCallback((index: number) => {
    setAttachments((prev) => prev.filter((_, j) => j !== index));
  }, []);

  const clear = useCallback(() => {
    setAttachments([]);
    setAttachError(null);
  }, []);

  const toBodyAttachments = useCallback(
    (): NewTicketAttachment[] =>
      attachments.map(({ filename, contentType, contentBase64 }) => ({
        filename,
        contentType,
        contentBase64,
      })),
    [attachments],
  );

  return {
    attachments,
    attachError,
    onFilesPicked,
    addFiles,
    addLocal,
    remove,
    clear,
    toBodyAttachments,
  };
}

// UI presentacional: chips de la selección (con botón de quitar), error de
// validación, e input file oculto + botón "📎 Adjuntar". No incluye el botón de
// publicar/crear: eso lo pone cada consumidor junto al resto de su footer.
export function AttachmentPickerUI({
  picker,
  disabled = false,
}: {
  picker: AttachmentPicker;
  disabled?: boolean;
}) {
  const { attachments, attachError, onFilesPicked, remove } = picker;
  const fileInputRef = useRef<HTMLInputElement>(null);

  return (
    <>
      {attachments.length > 0 && (
        <ul
          style={{
            listStyle: "none",
            margin: "0.4rem 0 0",
            padding: 0,
            display: "flex",
            flexWrap: "wrap",
            gap: "0.4rem",
          }}
        >
          {attachments.map((a, i) => (
            <li key={`${a.filename}-${i}`} style={chip}>
              <span
                title={a.filename}
                style={{
                  maxWidth: 180,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                📎 {a.filename}
              </span>
              <span style={{ color: "#73879C" }}>{formatBytes(a.size)}</span>
              <button
                type="button"
                aria-label={`Quitar ${a.filename}`}
                onClick={() => remove(i)}
                style={chipRemove}
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
      {attachError && <ErrorBox>{attachError}</ErrorBox>}
      <div style={{ marginTop: "0.4rem" }}>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          style={{ display: "none" }}
          onChange={onFilesPicked}
        />
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={disabled || attachments.length >= MAX_ATTACHMENTS}
          style={attachBtn}
          title="Adjuntar documentos (máx. 5, 10 MB c/u, 20 MB total)"
        >
          📎 Adjuntar
        </button>
      </div>
    </>
  );
}

const chip: React.CSSProperties = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4rem",
  border: "1px solid #dee2e6",
  borderRadius: "0.4rem",
  background: "white",
  color: "#1f2933",
  padding: "0.25rem 0.5rem",
  fontSize: ".76rem",
  cursor: "pointer",
};

const chipRemove: React.CSSProperties = {
  border: "none",
  background: "transparent",
  color: "#73879C",
  cursor: "pointer",
  fontSize: ".9rem",
  lineHeight: 1,
  padding: 0,
};

const attachBtn: React.CSSProperties = {
  padding: "0.4rem 0.7rem",
  borderRadius: "0.4rem",
  background: "white",
  color: "#0069d9",
  border: "1px solid #dee2e6",
  fontSize: ".82rem",
  fontWeight: 600,
  cursor: "pointer",
};
